import React from 'react';
import {
  Box,
  Typography,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

/**
 * Pagination controls component for paginated lists.
 *
 * Displays:
 * - "Showing X-Y of Z" text
 * - Page size selector
 * - Page navigation controls
 *
 * @param {object} props
 * @param {number} props.page - Current page number (1-indexed)
 * @param {number} props.totalPages - Total number of pages
 * @param {number} props.totalCount - Total number of items
 * @param {number} props.pageSize - Current page size
 * @param {function} props.onPageChange - Callback when page changes
 * @param {function} props.onPageSizeChange - Callback when page size changes
 * @param {boolean} props.loading - Whether data is loading
 * @param {number[]} props.pageSizeOptions - Available page size options (default: [25, 50, 100])
 */
const PaginationControls = ({
  page,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
  onPageSizeChange,
  loading = false,
  pageSizeOptions = [25, 50, 100],
}) => {
  const { t } = useTranslation();

  // Calculate showing range
  const from = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, totalCount);

  // Don't render if no items
  if (totalCount === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mt: 2,
        px: 1,
        flexWrap: 'wrap',
        gap: 2,
      }}
    >
      {/* Showing X-Y of Z */}
      <Typography variant="body2" color="text.secondary">
        {t('Showing {{from}}-{{to}} of {{total}}', { from, to, total: totalCount })}
      </Typography>

      {/* Page size selector and pagination */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {/* Page size selector */}
        <FormControl size="small" sx={{ minWidth: 80 }}>
          <InputLabel id="page-size-label" sx={{ display: 'none' }}>
            {t('Items per page')}
          </InputLabel>
          <Select
            labelId="page-size-label"
            value={pageSize}
            onChange={(e) => onPageSizeChange(e.target.value)}
            disabled={loading}
            size="small"
          >
            {pageSizeOptions.map((size) => (
              <MenuItem key={size} value={size}>
                {size}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Page navigation */}
        <Pagination
          count={totalPages}
          page={page}
          onChange={(_, newPage) => onPageChange(newPage)}
          disabled={loading}
          color="primary"
          showFirstButton
          showLastButton
          size="small"
          siblingCount={1}
          boundaryCount={1}
        />
      </Box>
    </Box>
  );
};

export default PaginationControls;
